import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { storage, db } from '../firebase/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const PaymentPage = ({ onPaymentSuccess, onBack }) => {
  const [paymentProof, setPaymentProof] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionMessage, setSubmissionMessage] = useState('');
  const [formData, setFormData] = useState({
    userEmail: '',
    userName: '',
    contactPhone: ''
  });
  const [validationErrors, setValidationErrors] = useState({
    userEmail: '',
    userName: '',
    contactPhone: ''
  });
  const { currentUser } = useAuth();

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhoneNumber = (phone) => {
    // Accept any phone number with digits, spaces, hyphens, parentheses, and plus sign
    const phoneRegex = /^[\d\s\-+()]+$/;
    const cleanPhone = phone.replace(/\s/g, '');
    // Must have at least 7 digits (minimum viable phone number)
    const digitCount = (cleanPhone.match(/\d/g) || []).length;
    return phoneRegex.test(phone) && digitCount >= 7;
  };

  const validateName = (name) => {
    // English characters only (letters, spaces, hyphens)
    const englishNameRegex = /^[a-zA-Z\s\-']+$/;
    return englishNameRegex.test(name.trim()) && name.trim().length >= 2;
  };

  const isFormValid = () => {
    return (
      validateEmail(formData.userEmail) &&
      validateName(formData.userName) &&
      validatePhoneNumber(formData.contactPhone) &&
      paymentProof !== null &&
      !validationErrors.userEmail &&
      !validationErrors.userName &&
      !validationErrors.contactPhone
    );
  };

  // Auto-fill email when component mounts
  useEffect(() => {
    if (currentUser?.email) {
      setFormData(prev => ({
        ...prev,
        userEmail: currentUser.email
      }));
    }
  }, [currentUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Real-time validation
    setValidationErrors(prev => ({
      ...prev,
      [name]: ''
    }));

    // Validate field based on name
    let error = '';
    switch (name) {
      case 'userEmail':
        if (value && !validateEmail(value)) {
          error = 'အီးမေးလ်လိပ်စာ မှန်ကန်မှု မရှိပါ (example@email.com ပုံစံ အတိုင်း ရိုက်ထည့်ပါ)';
        }
        break;
      case 'userName':
        if (value && !validateName(value)) {
          error = 'အမည်ကို အင်္ဂလိပ်လို သာ ရိုက်ထည့်ပါ';
        }
        break;
      case 'contactPhone':
        if (value && !validatePhoneNumber(value)) {
          error = 'ဖုန်းနံပါတ် မှန်ကန်မှု မရှိပါ (နံပါတ်များ သာ ရိုက်ထည့်ပါ)';
        }
        break;
      default:
        // No validation needed for other fields
        break;
    }

    setValidationErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setPaymentProof(file);
      setSubmissionMessage('');
    }
  };

  const handleSubmitPayment = async () => {
    // Final validation before submission
    const errors = {};
    
    if (!formData.userEmail.trim()) {
      errors.userEmail = 'အီးမေးလ်လိပ်စာ လိုအပ်ပါသည်';
    } else if (!validateEmail(formData.userEmail)) {
      errors.userEmail = 'အီးမေးလ်လိပ်စာ မှန်ကန်မှု မရှိပါ';
    }

    if (!formData.userName.trim()) {
      errors.userName = 'အမည် လိုအပ်ပါသည်';
    } else if (!validateName(formData.userName)) {
      errors.userName = 'အမည်ကို အင်္ဂလိပ်လို သာ ရိုက်ထည့်ပါ';
    }

    if (!formData.contactPhone.trim()) {
      errors.contactPhone = 'ဖုန်းနံပါတ် လိုအပ်ပါသည်';
    } else if (!validatePhoneNumber(formData.contactPhone)) {
      errors.contactPhone = 'ဖုန်းနံပါတ် မှန်ကန်မှု မရှိပါ (နံပါတ်များ သာ ရိုက်ထည့်ပါ)';
    }

    if (!paymentProof) {
      setSubmissionMessage('ကျေးဇူးပြု၍ ငွေပေးချေမှု screenshot ကို upload လုပ်ပါ။');
      return;
    }

    // Set validation errors if any
    setValidationErrors(errors);
    
    // If there are validation errors, don't submit
    if (Object.keys(errors).length > 0) {
      setSubmissionMessage('ကျေးဇူးပြု၍ အမှားများကို ပြင်ဆင်ပြီး ပြန်လည် ကြိုးစားပါ။');
      return;
    }

    setIsSubmitting(true);
    setSubmissionMessage('');
    
    try {
      // Step 1: Upload image to Firebase Storage
      const timestamp = Date.now();
      const fileName = `payment_${formData.userEmail}_${timestamp}.${paymentProof.name.split('.').pop()}`;
      const storageRef = ref(storage, `payment_screenshots/${fileName}`);
      
      setSubmissionMessage('ဓာတ်ပုံ upload လုပ်နေပါသည်...');
      const uploadResult = await uploadBytes(storageRef, paymentProof);
      
      // Step 2: Get the download URL
      const imageUrl = await getDownloadURL(uploadResult.ref);
      
      setSubmissionMessage('Payment request တင်ပို့နေပါသည်...');
      
      // Step 3: Save payment request to Firestore
      const paymentRequest = {
        email: formData.userEmail,
        userName: formData.userName,
        phoneNumber: formData.contactPhone,
        imageLink: imageUrl,
        fileName: fileName,
        time: serverTimestamp(),
        status: 'pending',
        amount: '₩30,000',
        currency: 'KRW'
      };

      const docRef = await addDoc(collection(db, 'payment_requests'), paymentRequest);
      
      setIsSubmitting(false);
      setSubmissionMessage(`ငွေပေးချေမှု အတည်ပြုချက် တင်ပို့ပြီးပါပြီ! Request ID: ${docRef.id.slice(0, 8)}... အတည်ပြုချက်ပြီးနောက် ၂၄ နာရီအတွင်း premium အသုံးပြုခွင့်ရရှိပါမည်။`); 
    } catch (error) {
      console.error('Error submitting payment:', error);
      setIsSubmitting(false);
      setSubmissionMessage('အမှားအယွင်း ဖြစ်ပေါ်ခဲ့ပါသည်။ ကျေးဇူးပြု၍ ပြန်လည် ကြိုးစားပါ။');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center mb-8">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>Main Page သို့ ပြန်သွားရန်</span>
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Premium Package Information */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="text-center mb-6">
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-4 rounded-lg mb-4">
                <h2 className="text-2xl font-bold">Premium အသုံးပြုခွင့်</h2>
                <p className="text-lg">(၄ လ)</p>
              </div>
              <div className="space-y-2">
                <div className="text-2xl text-gray-400 line-through">₩40,000</div>
                <div className="text-4xl font-bold text-purple-600 mb-2">₩30,000</div>
                <div className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-semibold inline-block mb-2">
                  25% လျှော့စျေး!
                </div>
              </div>
              <p className="text-gray-600">၄ လတာ Premium အသုံးပြုခွင့်</p>
            </div>

            <div className="space-y-4 mb-6">
              <h3 className="font-semibold text-gray-800 text-lg">ရရှိမည့် အကျိုးကျေးဇူးများ:</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <div className="bg-green-100 p-1 rounded-full">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-gray-700">Premium Content အားလုံးကို ဝင်ရောက်ခွင့်</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="bg-green-100 p-1 rounded-full">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-gray-700">အဆင့်မြင့် calculus ပြဿနာများနှင့် အဖြေများ</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="bg-green-100 p-1 rounded-full">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-gray-700">အသေးစိတ် အဆင့်ဆင့် ရှင်းလင်းချက်များ</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="bg-green-100 p-1 rounded-full">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-gray-700">အထူးလျှော့စျေး - ₩10,000!</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Payment Information */}
          <div className="space-y-6">
            {/* Bank Transfer Information */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <svg className="w-6 h-6 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                ငွေလွှဲပေးပို့ခြင်း အသေးစိတ်
              </h3>
              
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="font-semibold text-gray-700">ဘဏ်အမည်:</p>
                      <p className="text-gray-900">KB Bank (국민 은행) </p>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-700">အကောင့်ပိုင်ရှင်အမည်:</p>
                      <p className="text-gray-900">KHIT MIN</p>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-700">အကောင့်နံပါတ်:</p>
                      <p className="text-gray-900 font-mono">031701-00-014305</p>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-700">ငွေပမာဏ:</p>
                      <p className="text-gray-900 font-bold">₩30,000 KRW</p>
                      <p className="text-sm text-gray-600">(25% လျှော့စျေး)</p>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                  <h4 className="font-semibold text-yellow-800 mb-2">သတိပြုရန်:</h4>
                  <ul className="text-sm text-yellow-700 space-y-1">
                    <li>• ဘဏ်အကောင့်နဲ့ နာမည် မှန်မမှန် သေချာစွာ စစ်ဆေးပြီးမှ ငွေပေးချေပါ။</li>
                    <li>• ငွေပေးချေမှုကို ဆောင်ရွက်ပြီးပါက ရှင်းလင်းစွာ ဓာတ်ပုံ ရိုက်ပါ</li>
                    <li>• အောက်ပါ Payment Form ကိုဖြည့်စွက်ပေးပို့ပါ။</li>
                    <li>• ၂၄ နာရီအတွင်း စီစစ်ပေးပါမည်။</li>
                    <li>• Email မှတစ်ဆင့် ပြန်လည်ဆက်သွယ်ပါမည်။</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Payment Form */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <svg className="w-6 h-6 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Payment အတည်ပြုချက် Form
              </h3>
              
              <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); handleSubmitPayment(); }}>
                {/* Email Field */}
                <div>
                  <label htmlFor="userEmail" className="block text-sm font-medium text-gray-700 mb-1">
                    အီးမေးလ်လိပ်စာ *
                  </label>
                  <input
                    type="email"
                    id="userEmail"
                    name="userEmail"
                    value={formData.userEmail}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 ${
                      validationErrors.userEmail 
                        ? 'border-red-500 bg-red-50' 
                        : validateEmail(formData.userEmail) && formData.userEmail 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-300 bg-gray-50'
                    }`}
                    placeholder="example@email.com"
                    readOnly={!!currentUser?.email}
                  />
                  {validationErrors.userEmail && (
                    <p className="text-xs text-red-600 mt-1 flex items-center">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {validationErrors.userEmail}
                    </p>
                  )}
                </div>

                {/* Name Field */}
                <div>
                  <label htmlFor="userName" className="block text-sm font-medium text-gray-700 mb-1">
                    အမည် * (အင်္ဂလိပ်လို သာ)
                  </label>
                  <input
                    type="text"
                    id="userName"
                    name="userName"
                    value={formData.userName}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 ${
                      validationErrors.userName 
                        ? 'border-red-500 bg-red-50' 
                        : validateName(formData.userName) && formData.userName 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-300'
                    }`}
                    placeholder="John Doe"
                  />
                  {validationErrors.userName && (
                    <p className="text-xs text-red-600 mt-1 flex items-center">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {validationErrors.userName}
                    </p>
                  )}
                </div>

                {/* Phone Field */}
                <div>
                  <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700 mb-1">
                    ဖုန်းနံပါတ် *
                  </label>
                  <input
                    type="tel"
                    id="contactPhone"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 ${
                      validationErrors.contactPhone 
                        ? 'border-red-500 bg-red-50' 
                        : validatePhoneNumber(formData.contactPhone) && formData.contactPhone 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-300'
                    }`}
                    placeholder="+1 234 567 8900 (သို့) 09xxxxxxxxx"
                  />
                  {validationErrors.contactPhone && (
                    <p className="text-xs text-red-600 mt-1 flex items-center">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {validationErrors.contactPhone}
                    </p>
                  )}
                </div>

                {/* File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Screenshot *
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-purple-400 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="payment-receipt"
                      required
                    />
                    <label htmlFor="payment-receipt" className="cursor-pointer">
                      <div className="space-y-2">
                        <svg className="w-8 h-8 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <div className="text-gray-600 text-sm">
                          <span className="font-medium text-purple-600">နှိပ်ပြီး upload လုပ်ရန်</span>
                        </div>
                        <div className="text-xs text-gray-500">PNG, JPG ဖိုင်များ</div>
                      </div>
                    </label>
                  </div>

                  {paymentProof && (
                    <div className="mt-2 bg-green-50 p-3 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-green-800 text-sm">ဖိုင် upload ပြီးပါပြီ: {paymentProof.name}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting || !isFormValid()}
                  className={`w-full py-3 px-4 rounded-lg font-semibold transition-all ${
                    isSubmitting || !isFormValid()
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white transform hover:scale-105'
                  }`}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="animate-spin h-5 w-5 text-current" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>တင်ပို့နေသည်...</span>
                    </div>
                  ) : !isFormValid() ? (
                    'အချက်အလက်များကို မှန်ကန်စွာ ဖြည့်ပေးပါ။'
                  ) : (
                    'ငွေပေးချေမှု အတည်ပြုရန်'
                  )}
                </button>

                {/* Form Validation Status */}
                {!isFormValid() && (
                  <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg">
                    <h4 className="text-yellow-800 font-medium text-sm mb-2">အောက်ပါ အချက်များကို စစ်ဆေးပါ:</h4>
                    <ul className="text-sm text-yellow-700 space-y-1">
                      {!validateEmail(formData.userEmail) && (
                        <li className="flex items-center space-x-2">
                          <svg className="w-3 h-3 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          <span>အီးမေးလ်လိပ်စာ မှန်ကန်မှု</span>
                        </li>
                      )}
                      {!validateName(formData.userName) && (
                        <li className="flex items-center space-x-2">
                          <svg className="w-3 h-3 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          <span>အမည် အင်္ဂလိပ်လို ရေးသားမှု</span>
                        </li>
                      )}
                      {!validatePhoneNumber(formData.contactPhone) && (
                        <li className="flex items-center space-x-2">
                          <svg className="w-3 h-3 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          <span>ဖုန်းနံပါတ် ပုံစံ မှန်ကန်မှု</span>
                        </li>
                      )}
                      {!paymentProof && (
                        <li className="flex items-center space-x-2">
                          <svg className="w-3 h-3 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          <span>Payment Screenshot Upload</span>
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                {submissionMessage && (
                  <div className={`p-4 rounded-lg ${
                    submissionMessage.includes('တင်ပို့ပြီးပါပြီ') 
                      ? 'bg-green-50 border border-green-200 text-green-800' 
                      : 'bg-red-50 border border-red-200 text-red-800'
                  }`}>
                    {submissionMessage}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;